// ============================================================================
// === Configuration principale de la carte ===
// ============================================================================

import React, { useRef, memo, useCallback, useState, useEffect, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapContainer } from './styles';
import '../../styles/marker-animations.css';
import { useMapEvents } from './useMapEvents';
import { UnifiedMarkerModal } from '../modal/UnifiedMarkerModal';
import { useMarkers } from '../../hooks';
import { useMapInitialization } from '../../hooks/useMapInitialization';
import Notification from '../common/Notification';
import { MapError } from '../../types/mapTypes';
import SearchBar from './SearchBar';
import styled from 'styled-components';
import { ExtendedMarkerData, MarkerFormData } from '../../types/marker';
import { useReverseGeocoding } from '../../hooks/useReverseGeocoding';
import { eventsService } from '../../services/events.service';
import { ZoneManager } from '../prospection';
import ZoneDrawing from './ZoneDrawing';
import { SectionErrorBoundary } from '../common/SectionErrorBoundary';
import type { DrawMode, ProspectionZone, Coordinate } from '../../types/prospectionZone';
import logger from '../../utils/logger';

/**
 * Fonction utilitaire simple pour limiter la fréquence d'appel d'une fonction
 * @param fn Fonction à exécuter
 * @param delay Délai en ms
 */
const debounce = <F extends (...args: any[]) => any>(fn: F, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return function(...args: Parameters<F>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  };
};
import { useUserProfile } from '../../hooks/useUserProfile';
import useWindowSize from '../../hooks/useWindowSize';
import { doWithRef } from '../../utils/refUtils';

// Container pour regrouper la barre de recherche
const MapControlsContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1001; /* Augmenter le z-index pour qu'il soit au-dessus de la sidebar */
  display: flex;
  align-items: center;
`;

// Bouton d'ajout de catégorie
const _AddCategoryButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #2c3e50;
  color: white;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s, transform 0.2s;
  margin-left: 6px; /* Réduire l'écart avec le bouton précédent */
  z-index: 1002;
  
  &:hover {
    background-color: #34495e;
    transform: scale(1.05);
  }
  
  svg {
    font-size: 18px;
  }
  
  &:focus {
    outline: none;
  }
  
  @media (min-width: 1025px) {
    display: none; /* Cacher le bouton sur desktop */
  }
`;

interface MapComponentProps {
  onMapInit: (mapRef: React.MutableRefObject<mapboxgl.Map | null>) => void;
  onAddCategory?: () => void;
}

const MapComponent: React.FC<MapComponentProps> = ({ onMapInit, onAddCategory }) => {
  logger.debug('🔄 MapComponent render');
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<mapboxgl.Map | null>(null);
  const [error, setError] = useState<MapError | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { userProfile } = useUserProfile();
  const { isMobile: _isMobile } = useWindowSize();
  const _isOwner = userProfile?.is_owner || false;
  
  // États pour les zones de prospection
  const [drawMode, setDrawMode] = useState<DrawMode>('none');
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [zones, setZones] = useState<ProspectionZone[]>([]);
  
  const { isModalOpen, clickedCoordinates, handleMapClick, handleCloseModal } = useMapEvents({
    isDrawingMode: drawMode === 'drawing'
  });

  const handleError = useCallback((error: MapError) => {
    setError(error);
  }, []);

  // Log de montage/démontage
  useEffect(() => {
    logger.debug('🟢 MapComponent MONTÉ');
    return () => {
      logger.debug('🔴 MapComponent DÉMONTÉ');
    };
  }, []);

  // Nettoyer le conteneur avant l'initialisation de la carte
  useEffect(() => {
    doWithRef(mapContainer, container => {
      container.innerHTML = '';
    });
  }, []);

  // Points de dessin temporaires
  const [drawingPoints, setDrawingPoints] = useState<[number, number][]>([]);

  // Gestionnaires pour les zones de prospection
  const handlePointAdded = useCallback((coordinate: Coordinate) => {
    logger.debug('Point ajouté:', coordinate);
  }, []);

  const handleDrawingComplete = useCallback((coordinates: Coordinate[]) => {
    logger.debug('Dessin terminé', { points: coordinates.length });
    // Si coordinates est vide, c'est un reset (annulation ou sauvegarde)
    if (coordinates.length === 0) {
      setDrawingPoints([]);
      eventsService.emit('DRAWING_COORDINATES_UPDATE', { coordinates: [] });
    } else {
      // Sinon, c'est la fermeture du polygone
      setDrawingPoints(coordinates);
      eventsService.emit('DRAWING_COORDINATES_UPDATE', { coordinates });
      logger.debug('Événement DRAWING_COORDINATES_UPDATE émis', { points: coordinates.length });
      
      // Émettre l'événement de fin de dessin pour ouvrir le modal
      eventsService.emit('ZONE_DRAWING_COMPLETE', { coordinates });
      logger.debug('✅ Événement ZONE_DRAWING_COMPLETE émis → Le modal va s\'ouvrir');
    }
  }, []);

  const handleZonesLoaded = useCallback((loadedZones: ProspectionZone[]) => {
    setZones(loadedZones);
  }, []);

  // Écouter l'événement pour démarrer le dessin de zone
  useEffect(() => {
    const handleStartDrawing = () => {
      logger.debug('🎨 Événement START_ZONE_DRAWING reçu dans MapComponent');
      logger.debug('🎨 Activation du mode dessin');
      setDrawMode('drawing');
    };

    const handleCancelDrawing = () => {
      logger.debug('🧹 Événement CANCEL_ZONE_DRAWING reçu dans MapComponent');
      logger.debug('🧹 Désactivation du mode dessin et nettoyage');
      setDrawMode('none');
      setDrawingPoints([]);
    };

    const handleReloadZones = () => {
      logger.debug('🔄 Événement RELOAD_ZONES reçu dans MapComponent');
      // Forcer le rechargement en mettant à jour l'état zones avec une copie
      setZones(prev => [...prev]);
    };

    const unsubscribeStart = eventsService.subscribe('START_ZONE_DRAWING', handleStartDrawing, 'map-component-zone-drawing');
    const unsubscribeCancel = eventsService.subscribe('CANCEL_ZONE_DRAWING', handleCancelDrawing, 'map-component-cancel-drawing');
    const unsubscribeReload = eventsService.subscribe('RELOAD_ZONES', handleReloadZones, 'map-component-reload-zones');

    return () => {
      unsubscribeStart();
      unsubscribeCancel();
      unsubscribeReload();
    };
  }, []);

  // Créer un gestionnaire de clic personnalisé qui gère le mode dessin
  const handleMapClickWithDrawing = useCallback((e: mapboxgl.MapMouseEvent) => {
    if (drawMode === 'drawing') {
      // En mode dessin, ajouter un point
      const newPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      logger.debug('🖱️ Clic en mode dessin:', newPoint);
      
      setDrawingPoints(prev => {
        const updated = [...prev, newPoint];
        logger.debug('📍 Total points:', updated.length);
        handlePointAdded(newPoint);
        
        // Vérifier si on ferme le polygone (clic proche du premier point)
        if (updated.length >= 4) {
          const firstPoint = updated[0];
          const distance = Math.sqrt(
            Math.pow(newPoint[0] - firstPoint[0], 2) +
            Math.pow(newPoint[1] - firstPoint[1], 2)
          );
          
          if (distance < 0.0005) {
            logger.debug('✅ Polygone fermé!');
            handleDrawingComplete([...updated, firstPoint]);
            return [];
          }
        }
        
        return updated;
      });
      return;
    }
    // Sinon, comportement normal
    handleMapClick(e);
  }, [drawMode, handleMapClick, handlePointAdded, handleDrawingComplete]);

  useMapInitialization(mapContainer, mapInstanceRef, handleError, handleMapClickWithDrawing);
  
  // Changer le curseur en mode dessin
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    const canvas = mapInstanceRef.current.getCanvas();
    if (!canvas) return;
    
    if (drawMode === 'drawing') {
      logger.debug('🎨 Activation du mode dessin - curseur croix');
      canvas.style.cursor = 'crosshair';
    } else {
      canvas.style.cursor = '';
    }
  }, [drawMode, mapInstanceRef]);
  
  /**
   * Fonction globale pour nettoyer les éléments d'attribution Mapbox et ajuster la carte
   * Cette fonction est utilisée à plusieurs endroits pour garantir que la carte s'affiche correctement
   * sans bande à droite ni bouton de téléchargement, en particulier en mode mobile et lors des
   * transitions d'ouverture/fermeture de la sidebar
   */
  const cleanupAndAdjust = useCallback(() => {
    const isMobile = window.innerWidth <= 768;
    
    // Ajuster les styles du conteneur de la carte
    doWithRef(mapContainer, container => {
      // Déterminer la largeur en fonction de l'état de la sidebar et du mode (mobile/desktop)
      const width = sidebarCollapsed || isMobile ? '100%' : 'calc(100% - 300px)';
      
      // Appliquer les styles de base au conteneur
      Object.assign(container.style, {
        width,
        left: '0',
        right: '0',
        overflow: 'hidden'
      });
      
      // Sélecteurs pour les éléments à masquer/supprimer
      const selectors = {
        attribution: '.mapboxgl-ctrl-attrib, .mapboxgl-ctrl-attrib-button',
        rightControls: '.mapboxgl-ctrl-top-right',
        canvas: '.mapboxgl-canvas'
      };
      
      // 1. Masquer et supprimer les éléments d'attribution
      const attribElements = document.querySelectorAll(selectors.attribution);
      attribElements.forEach(el => {
        // D'abord masquer avec CSS (approche défensive)
        Object.assign((el as HTMLElement).style, {
          display: 'none',
          visibility: 'hidden',
          opacity: '0',
          width: '0',
          height: '0'
        });
        
        // Puis supprimer complètement du DOM
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
      
      // 2. Masquer les contrôles à droite (toujours, pas seulement en mobile)
      const rightControls = document.querySelector(selectors.rightControls);
      if (rightControls) {
        (rightControls as HTMLElement).style.display = 'none';
      }
      
      // 3. S'assurer que le canvas prend toute la largeur disponible
      const canvas = container.querySelector(selectors.canvas);
      if (canvas) {
        Object.assign((canvas as HTMLElement).style, {
          width: '100%',
          height: '100%',
          left: '0',
          right: '0'
        });
      }
    });
    
    // Redimensionner la carte pour qu'elle s'adapte à son conteneur
    doWithRef(mapInstanceRef, map => map.resize());
  }, [mapContainer, mapInstanceRef, sidebarCollapsed]);
  
  // Gestionnaire pour s'assurer que la carte s'adapte correctement lors des changements
  useEffect(() => {
    // Délai d'attente pour les différentes étapes de nettoyage
    const delays = { initial: 500, resize: 300 };
    
    // 1. Exécuter immédiatement au montage du composant
    cleanupAndAdjust();
    
    // 2. Exécuter après un court délai pour s'assurer que tout est chargé
    const initialTimeout = setTimeout(cleanupAndAdjust, delays.initial);
    
    // 3. Configurer un observateur de mutations pour les changements dynamiques du DOM
    const observer = new MutationObserver(cleanupAndAdjust);
    observer.observe(document.body, { childList: true, subtree: true });
    
    // 4. Gérer les changements de taille de la fenêtre
    const handleResize = debounce(() => {
      cleanupAndAdjust();
      setTimeout(cleanupAndAdjust, delays.resize);
    }, 100);
    
    window.addEventListener('resize', handleResize);
    
    // Nettoyer les écouteurs et timeouts lors du démontage
    return () => {
      clearTimeout(initialTimeout);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [cleanupAndAdjust]);

  // Écouter les événements de repli/déploiement de la sidebar
  useEffect(() => {
    // Délais pour les différentes étapes de l'animation de la sidebar
    const delays = { initial: 0, during: 300, after: 600 };
    
    const handleSidebarToggle = (data: { isCollapsed: boolean }) => {
      setSidebarCollapsed(data.isCollapsed);
      
      // Appliquer les ajustements à différents moments de l'animation
      // pour garantir que la carte reste correctement affichée
      [delays.initial, delays.during, delays.after].forEach(delay => {
        setTimeout(cleanupAndAdjust, delay);
      });
    };
    
    // S'abonner aux événements de la sidebar
    const unsubscribe = eventsService.subscribe('SIDEBAR_TOGGLE' as any, handleSidebarToggle, 'map-component');
    
    return unsubscribe;
  }, [cleanupAndAdjust]);
  
  const { 
    addMarker,
    isEditModalOpen,
    selectedMarker,
    handleCloseEditModal,
    handleSaveMarker: saveMarkerToState,
    fitMapToMarkers: _fitMapToMarkers 
  } = useMarkers(mapInstanceRef);

  // Récupérer l'adresse à partir des coordonnées
  const { address: markerAddress, loading: addressLoading } = useReverseGeocoding(
    clickedCoordinates ? clickedCoordinates[1] : 0,  // latitude
    clickedCoordinates ? clickedCoordinates[0] : 0   // longitude
  );

  useEffect(() => {
    doWithRef(mapInstanceRef, () => {
      onMapInit(mapInstanceRef);
    });
  }, [mapInstanceRef, onMapInit]);

  const handleCreateMarker = useCallback(async (coordinates: [number, number], data: MarkerFormData): Promise<ExtendedMarkerData> => {
    try {
      if (addressLoading) {
        throw new Error("Chargement de l'adresse en cours");
      }

      if (!markerAddress) {
        handleError({
          message: "Impossible de récupérer l'adresse pour ce point",
          code: 'GEOCODING_ERROR',
          severity: 'error'
        });
        throw new Error("Adresse non disponible");
      }

      const createdMarker = await addMarker(coordinates, {
        ...data,
        address: markerAddress
      });

      handleCloseModal();
      return createdMarker;
    } catch (error) {
      console.error('Erreur lors de la création du marker:', error);
      handleError({
        message: "Erreur lors de la création du marker",
        code: 'MARKER_CREATE_ERROR',
        severity: 'error'
      });
      throw error;
    }
  }, [addMarker, handleCloseModal, handleError, markerAddress, addressLoading]);

  const handleSave = useCallback(async (data: MarkerFormData): Promise<ExtendedMarkerData> => {
    if (clickedCoordinates) {
      const createdMarker = await handleCreateMarker(clickedCoordinates, data);
      return createdMarker;
    }
    throw new Error('Coordonnées non définies');
  }, [clickedCoordinates, handleCreateMarker]);

  const handleEditSave = useCallback(async (data: ExtendedMarkerData): Promise<ExtendedMarkerData> => {
    const updatedMarker = await saveMarkerToState(data);
    return updatedMarker;
  }, [saveMarkerToState]);

  const createModalProps = useMemo(() => ({
    isOpen: isModalOpen,
    mode: 'create' as const,
    onClose: handleCloseModal,
    onSave: handleSave,
    markerAddress
  }), [isModalOpen, handleCloseModal, handleSave, markerAddress]);

  const editModalProps = useMemo(() => ({
    isOpen: isEditModalOpen,
    mode: 'edit' as const,
    onClose: handleCloseEditModal,
    onSave: handleEditSave,
    marker: selectedMarker || undefined,
    _actionId: selectedMarker?._actionId,
    _notificationId: selectedMarker?.notificationId,
    _notificationTitle: selectedMarker?.notificationTitle
  }), [isEditModalOpen, handleCloseEditModal, handleEditSave, selectedMarker]);

  return (
    <>
      <MapControlsContainer>
        <SearchBar 
          map={mapInstanceRef} 
          onAddCategory={onAddCategory} 
          sidebarCollapsed={sidebarCollapsed}
        />
      </MapControlsContainer>
      
      {/* Gestionnaire de zones (boutons + formulaire + liste) */}
      <ZoneManager
        drawMode={drawMode}
        selectedZoneId={selectedZoneId}
        drawingCoordinates={drawingPoints}
        onDrawModeChange={setDrawMode}
        onZoneSelect={setSelectedZoneId}
        onPointAdded={handlePointAdded}
        onDrawingComplete={handleDrawingComplete}
        onZonesLoaded={handleZonesLoaded}
      />
      
      {/* Composant de dessin des zones (invisible, gère la logique) */}
      {mapInstanceRef.current && (
        <SectionErrorBoundary 
          sectionName="Dessin de zones"
          fallbackMessage="Erreur lors du dessin des zones. Les zones existantes restent visibles."
        >
          <ZoneDrawing
            map={mapInstanceRef.current}
            isDrawing={drawMode === 'drawing'}
            zones={zones}
            selectedZoneId={selectedZoneId}
            onPointAdded={handlePointAdded}
            onDrawingComplete={handleDrawingComplete}
          />
        </SectionErrorBoundary>
      )}
      
      <MapContainer 
        ref={mapContainer} 
        width="100%" // La largeur sera gérée dynamiquement par cleanupAndAdjust
        height="100vh"
        debug={false} // Désactiver la bordure rouge de débogage
      >
        {error && (
          <Notification
            message={error.message}
            type="error"
            onClose={() => setError(null)}
          />
        )}
        {isModalOpen && clickedCoordinates && (
          <UnifiedMarkerModal {...createModalProps} />
        )}
        {isEditModalOpen && selectedMarker && (
          <UnifiedMarkerModal {...editModalProps} />
        )}
      </MapContainer>
    </>
  );
};

// Fonction de comparaison personnalisée pour memo
const arePropsEqual = (prevProps: MapComponentProps, nextProps: MapComponentProps) => {
  logger.debug('🔍 memo arePropsEqual appelé');
  const isEqual = prevProps.onMapInit === nextProps.onMapInit && 
                  prevProps.onAddCategory === nextProps.onAddCategory;
  
  logger.debug('🔍 Props comparison:', {
    onMapInitEqual: prevProps.onMapInit === nextProps.onMapInit,
    onAddCategoryEqual: prevProps.onAddCategory === nextProps.onAddCategory,
    shouldSkipRender: isEqual
  });
  
  if (!isEqual) {
    logger.debug('⚠️ MapComponent props changed - RE-RENDER');
  } else {
    logger.debug('✅ Props identiques - SKIP RENDER');
  }
  
  return isEqual;
};

// Export avec memo pour éviter les re-renders inutiles
export default memo(MapComponent, arePropsEqual);

